import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCryptoWithRefresh, useCryptoStore } from '@/hooks/useCrypto'
import DataSection from '@/components/ui/DataSection'
import {
  RefreshCw, TrendingUp, TrendingDown, Bitcoin, ArrowUpRight,
  ArrowDownRight, Activity, Globe, Shield, Flame, BarChart3, ExternalLink,
  AlertCircle,
} from 'lucide-react'
import { format } from 'date-fns'
import { safeFormat } from '../lib/format'
import type { CryptoCoin, CryptoCoinDetail } from '@/lib/types'

// Map CoinGecko coin symbol -> yfinance ticker for the chart page
const CRYPTO_TO_YF: Record<string, string> = {
  BTC: 'BTC-USD',
  ETH: 'ETH-USD',
  SOL: 'SOL-USD',
  XRP: 'XRP-USD',
  ADA: 'ADA-USD',
  DOGE: 'DOGE-USD',
  DOT: 'DOT-USD',
  AVAX: 'AVAX-USD',
  LINK: 'LINK-USD',
  UNI: 'UNI-USD',
  MATIC: 'MATIC-USD',
  LTC: 'LTC-USD',
  BCH: 'BCH-USD',
  ATOM: 'ATOM-USD',
  FIL: 'FIL-USD',
  ICP: 'ICP-USD',
  APT: 'APT-USD',
  ARB: 'ARB-USD',
  OP: 'OP-USD',
  SUI: 'SUI-USD',
  TRX: 'TRX-USD',
  NEAR: 'NEAR-USD',
  VET: 'VET-USD',
  ALGO: 'ALGO-USD',
  HBAR: 'HBAR-USD',
  TON: 'TON11419-USD',
}

function yfTickerForCoin(symbol: string | null | undefined): string {
  const upper = (symbol ?? '').toUpperCase()
  return CRYPTO_TO_YF[upper] ?? `${upper}-USD`
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function fmt(n: number | undefined | null, opts?: Intl.NumberFormatOptions) {
  if (n == null) return '--'
  return n.toLocaleString('en-GB', { maximumFractionDigits: 2, ...opts })
}

function fmtCompact(n: number | undefined | null) {
  if (n == null) return '--'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  return `$${fmt(n)}`
}

function ChangeChip({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="text-text-muted">--</span>
  const pos = v >= 0
  return (
    <span className={pos ? 'delta-chip-pos' : 'delta-chip-neg'}>
      {pos ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {pos ? '+' : ''}{v.toFixed(2)}%
    </span>
  )
}

/* Fear & Greed gauge */
function FearGreedGauge({ value, label }: { value: number; label: string }) {
  const color = value >= 75 ? '#10b981' : value >= 55 ? '#22d3ee' : value >= 45 ? '#f59e0b' : value >= 25 ? '#f97316' : '#f43f5e'
  const r = 32
  const circ = Math.PI * r // half-circle
  const fill = circ - (value / 100) * circ
  return (
    <div className="flex flex-col items-center">
      <svg width="80" height="48" viewBox="0 0 80 48">
        <path d="M 8 44 A 32 32 0 0 1 72 44" fill="none" stroke="#1e293b" strokeWidth="7" strokeLinecap="round" />
        <path d="M 8 44 A 32 32 0 0 1 72 44" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={fill} />
        <text x="40" y="40" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#f1f5f9">{value}</text>
      </svg>
      <span className="text-xs text-text-muted mt-1">{label}</span>
    </div>
  )
}

/* Sparkline (simple SVG) */
function Sparkline({ data, color = '#60a5fa', w = 80, h = 24 }: { data: number[]; color?: string; w?: number; h?: number }) {
  if (!data.length) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ')
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  )
}

/* Correlation heatmap cell */
function CorrelationCell({ v }: { v: number }) {
  const abs = Math.abs(v)
  const bg =
    v >= 0.7 ? 'bg-emerald-500/60' :
    v >= 0.4 ? 'bg-emerald-500/30' :
    v >= 0.1 ? 'bg-emerald-500/15' :
    v >= -0.1 ? 'bg-slate-500/20' :
    v >= -0.4 ? 'bg-rose-500/15' :
    'bg-rose-500/30'
  return (
    <td className={`text-center text-xs p-1.5 ${bg} ${v === 1 ? 'font-bold' : ''}`}>
      {v.toFixed(2)}
    </td>
  )
}

/* ─── Coin Detail Modal ──────────────────────────────────────────────────── */

function CoinDetailPanel({ coin, onClose }: { coin: CryptoCoinDetail; onClose: () => void }) {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('7d')
  const historyKey = `price_history_${period}` as keyof CryptoCoinDetail
  const history = (coin[historyKey] as Array<{ timestamp: number; price: number }>) ?? []
  const prices = history.map(p => p.price)

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-text-primary">{coin.name} ({coin.symbol})</h3>
          <p className="text-text-muted text-xs">Rank #{coin.market_cap_rank}</p>
        </div>
        <button onClick={onClose} className="btn-secondary text-xs">Close</button>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-card">
          <p className="stat-label">Price</p>
          <p className="stat-value text-lg">${fmt(coin.price)}</p>
          <ChangeChip v={coin.change_24h} />
        </div>
        <div className="stat-card">
          <p className="stat-label">ATH</p>
          <p className="stat-value text-lg">${fmt(coin.ath)}</p>
          <ChangeChip v={coin.ath_change_pct} />
        </div>
        <div className="stat-card">
          <p className="stat-label">Market Cap</p>
          <p className="stat-value text-lg">{fmtCompact(coin.market_cap)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">24h Volume</p>
          <p className="stat-value text-lg">{fmtCompact(coin.volume_24h)}</p>
        </div>
      </div>

      {/* Chart period selector */}
      <div className="flex gap-2">
        {(['7d', '30d', '90d', '1y'] as const).map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-3 py-1 text-xs rounded-md ${period === p ? 'bg-blue/20 text-blue-light font-medium' : 'text-text-muted hover:bg-surface-card'}`}>
            {p.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Price chart */}
      {prices.length > 0 && (
        <div className="bg-surface-card rounded-lg p-4">
          <Sparkline data={prices} w={600} h={120}
            color={prices[prices.length - 1] >= prices[0] ? '#10b981' : '#f43f5e'} />
        </div>
      )}

      {/* Supply + Social */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><span className="text-text-muted">Circulating:</span> <span className="text-text-primary">{fmt(coin.circulating_supply, { maximumFractionDigits: 0 })}</span></div>
        <div><span className="text-text-muted">Total Supply:</span> <span className="text-text-primary">{coin.total_supply ? fmt(coin.total_supply, { maximumFractionDigits: 0 }) : '--'}</span></div>
        <div><span className="text-text-muted">Reddit:</span> <span className="text-text-primary">{fmt(coin.social?.reddit_subscribers, { maximumFractionDigits: 0 })}</span></div>
        <div><span className="text-text-muted">Twitter:</span> <span className="text-text-primary">{fmt(coin.social?.twitter_followers, { maximumFractionDigits: 0 })}</span></div>
      </div>

      {coin.description && (
        <p className="text-xs text-text-muted leading-relaxed">{coin.description}</p>
      )}
    </div>
  )
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */

export default function CryptoPage() {
  const { dashboard, defi, exchanges, whales, correlation, loading, refreshing, error } = useCryptoWithRefresh(30_000)
  const { fetchCoinDetail, coinDetail, coinLoading, fetchAll } = useCryptoStore()
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleCoinClick = async (coinId: string) => {
    setSelectedCoin(coinId)
    await fetchCoinDetail(coinId)
  }

  const handleChartNav = (e: React.MouseEvent, symbol: string) => {
    e.stopPropagation()
    navigate(`/app/chart?ticker=${encodeURIComponent(yfTickerForCoin(symbol))}`)
  }

  const global = dashboard?.global
  const fearGreed = dashboard?.fear_greed
  const topCoins = dashboard?.top_coins ?? []
  const gainers = dashboard?.gainers ?? []
  const losers = dashboard?.losers ?? []
  const trending = dashboard?.trending ?? []
  const exchangeList = exchanges?.exchanges ?? []
  const corr = correlation

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Bitcoin size={20} className="text-amber-400" aria-hidden />
            Crypto Dashboard
          </h1>
          <div className="flex items-center gap-2">
            {dashboard && (
              <>
                <p className="text-sm text-text-secondary">
                  Updated {safeFormat(dashboard.last_updated, 'HH:mm', format)}
                </p>
                <span className="chip-live">live</span>
              </>
            )}
            {refreshing && (
              <span className="text-xs text-text-muted flex items-center gap-1">
                <RefreshCw size={11} className="animate-spin" aria-hidden />
                refreshing...
              </span>
            )}
          </div>
        </div>
        <button type="button" onClick={() => fetchAll(true)} className="btn-secondary gap-1.5 text-xs" disabled={loading || refreshing}>
          <RefreshCw size={13} className={loading || refreshing ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* ── Market Overview Cards ─────────────────────────────────────────── */}
      <DataSection
        loading={loading && !dashboard}
        error={error && !dashboard ? error : null}
        onRetry={() => fetchAll(true)}
        skeletonRows={6}
      >
      {error && dashboard && (
        <div className="card p-4 border border-rose-500/30 bg-rose-500/5 flex items-center gap-2 text-sm">
          <AlertCircle size={15} className="text-rose-400 shrink-0" aria-hidden />
          <span className="text-rose-400">{error}</span>
          <span className="ml-auto text-text-muted text-xs">Showing last known data</span>
        </div>
      )}
      <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 stagger-children">
        <div className="stat-card">
          <p className="stat-label">Total Market Cap</p>
          <p className="stat-hero text-gradient">{fmtCompact(global?.total_market_cap)}</p>
          <ChangeChip v={global?.market_cap_change_24h} />
        </div>
        <div className="stat-card">
          <p className="stat-label">24h Volume</p>
          <p className="stat-value text-lg">{fmtCompact(global?.total_volume_24h)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">BTC Dominance</p>
          <p className="stat-value text-lg">{global?.btc_dominance?.toFixed(1) ?? '--'}%</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">ETH Dominance</p>
          <p className="stat-value text-lg">{global?.eth_dominance?.toFixed(1) ?? '--'}%</p>
        </div>
        <div className="stat-card items-center">
          <p className="stat-label">Fear & Greed</p>
          {fearGreed ? (
            <FearGreedGauge value={fearGreed.value} label={fearGreed.classification} />
          ) : (
            <p className="text-text-muted text-sm">--</p>
          )}
        </div>
      </div>

      {/* ── Coin Detail (if selected) ────────────────────────────────────── */}
      {selectedCoin && coinDetail && !coinLoading && (
        <CoinDetailPanel coin={coinDetail} onClose={() => setSelectedCoin(null)} />
      )}
      {selectedCoin && coinLoading && (
        <div className="card p-8 text-center text-text-muted text-sm">Loading coin data...</div>
      )}

      {/* ── Top 20 Coins Table ───────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <BarChart3 size={15} className="text-text-muted" aria-hidden />
          <h2 className="panel-title">Top 20 by Market Cap</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table-pro w-full text-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Coin</th>
                <th className="!text-right">Price</th>
                <th className="!text-right">24h</th>
                <th className="!text-right">7d</th>
                <th className="!text-right">Market Cap</th>
                <th className="!text-right">Volume</th>
              </tr>
            </thead>
            <tbody>
              {topCoins.map(coin => (
                <tr key={coin.id}
                  className="cursor-pointer"
                  onClick={() => handleCoinClick(coin.id)}>
                  <td className="text-text-muted">{coin.rank}</td>
                  <td className="font-medium text-text-primary">
                    <div className="flex items-center gap-2">
                      <span>{coin.name} <span className="text-text-muted">{coin.symbol}</span></span>
                      <button
                        type="button"
                        title={`View ${coin.symbol} chart`}
                        className="opacity-0 group-hover:opacity-100 hover:text-blue-400 text-text-muted transition-opacity"
                        onClick={(e) => handleChartNav(e, coin.symbol)}
                      >
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="text-right text-text-primary">${fmt(coin.price)}</td>
                  <td className="text-right"><ChangeChip v={coin.change_24h} /></td>
                  <td className="text-right"><ChangeChip v={coin.change_7d} /></td>
                  <td className="text-right text-text-secondary">{fmtCompact(coin.market_cap)}</td>
                  <td className="text-right text-text-secondary">{fmtCompact(coin.volume_24h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && topCoins.length === 0 && (
          <p className="p-8 text-center text-text-muted text-sm">No coin data available.</p>
        )}
      </div>

      {/* ── Gainers / Losers ─────────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Gainers */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <TrendingUp size={15} className="text-emerald-400" aria-hidden />
            <h2 className="panel-title">Top Gainers (24h)</h2>
          </div>
          <ul className="divide-y divide-border">
            {gainers.map(c => (
              <li key={c.id} className="px-5 py-3 flex items-center justify-between hover:bg-surface-card cursor-pointer group"
                onClick={() => handleCoinClick(c.id)}>
                <div className="flex items-center gap-2">
                  <div>
                    <span className="text-text-primary font-medium text-sm">{c.symbol}</span>
                    <span className="text-text-muted text-xs ml-2">{c.name}</span>
                  </div>
                  <button
                    type="button"
                    title={`View ${c.symbol} chart`}
                    className="opacity-0 group-hover:opacity-100 hover:text-blue-400 text-text-muted transition-opacity"
                    onClick={(e) => handleChartNav(e, c.symbol)}
                  >
                    <ExternalLink size={12} />
                  </button>
                </div>
                <div className="text-right">
                  <span className="text-text-primary text-sm">${fmt(c.price)}</span>
                  <div><ChangeChip v={c.change_24h} /></div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {/* Losers */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <TrendingDown size={15} className="text-rose-400" aria-hidden />
            <h2 className="panel-title">Top Losers (24h)</h2>
          </div>
          <ul className="divide-y divide-border">
            {losers.map(c => (
              <li key={c.id} className="px-5 py-3 flex items-center justify-between hover:bg-surface-card cursor-pointer group"
                onClick={() => handleCoinClick(c.id)}>
                <div className="flex items-center gap-2">
                  <div>
                    <span className="text-text-primary font-medium text-sm">{c.symbol}</span>
                    <span className="text-text-muted text-xs ml-2">{c.name}</span>
                  </div>
                  <button
                    type="button"
                    title={`View ${c.symbol} chart`}
                    className="opacity-0 group-hover:opacity-100 hover:text-blue-400 text-text-muted transition-opacity"
                    onClick={(e) => handleChartNav(e, c.symbol)}
                  >
                    <ExternalLink size={12} />
                  </button>
                </div>
                <div className="text-right">
                  <span className="text-text-primary text-sm">${fmt(c.price)}</span>
                  <div><ChangeChip v={c.change_24h} /></div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Trending Coins Carousel ──────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Flame size={15} className="text-orange-400" aria-hidden />
          <h2 className="panel-title">Trending</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto p-4">
          {trending.map((t, i) => (
            <div key={t.id}
              className="flex-shrink-0 w-36 stat-card cursor-pointer hover:border-blue/30 transition-colors hover-lift group"
              onClick={() => handleCoinClick(t.id)}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-text-muted">#{i + 1}</span>
                  <Flame size={10} className="text-orange-400" />
                </div>
                <button
                  type="button"
                  title={`View ${t.symbol} chart`}
                  className="opacity-0 group-hover:opacity-100 hover:text-blue-400 text-text-muted transition-opacity"
                  onClick={(e) => handleChartNav(e, t.symbol)}
                >
                  <ExternalLink size={11} />
                </button>
              </div>
              <p className="text-sm font-medium text-text-primary">{t.symbol}</p>
              <p className="text-xs text-text-muted truncate">{t.name}</p>
            </div>
          ))}
          {trending.length === 0 && (
            <p className="text-text-muted text-sm py-2">No trending data.</p>
          )}
        </div>
      </div>

      {/* ── BTC vs S&P 500 Correlation Matrix ────────────────────────────── */}
      {corr && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Globe size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">Crypto vs TradFi Correlation</h2>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="p-1.5"></th>
                  {corr.assets.map(a => (
                    <th key={a} className="p-1.5 text-text-muted font-medium">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {corr.assets.map((asset, i) => (
                  <tr key={asset}>
                    <td className="p-1.5 text-text-muted font-medium">{asset}</td>
                    {corr.matrix[i].map((v, j) => (
                      <CorrelationCell key={j} v={v} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Exchange Rankings ─────────────────────────────────────────────── */}
      {exchangeList.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Shield size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">Exchange Rankings</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted text-xs uppercase border-b border-border">
                  <th>#</th>
                  <th>Exchange</th>
                  <th className="!text-center">Trust Score</th>
                  <th className="!text-right">24h Vol (BTC)</th>
                  <th>Country</th>
                  <th className="!text-right">Est.</th>
                </tr>
              </thead>
              <tbody>
                {exchangeList.map(ex => (
                  <tr key={ex.id}>
                    <td className="text-text-muted">{ex.rank}</td>
                    <td className="font-medium text-text-primary">{ex.name}</td>
                    <td className="text-center">
                      <span className={`inline-block w-6 h-6 leading-6 text-center rounded-full text-xs font-bold ${
                        ex.trust_score >= 9 ? 'bg-emerald-500/20 text-emerald-400' :
                        ex.trust_score >= 7 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-rose-500/20 text-rose-400'
                      }`}>
                        {ex.trust_score}
                      </span>
                    </td>
                    <td className="text-right text-text-secondary">{fmt(ex.volume_24h_btc, { maximumFractionDigits: 0 })}</td>
                    <td className="text-text-muted">{ex.country || '--'}</td>
                    <td className="text-right text-text-muted">{ex.year_established ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Whale Activity Feed ──────────────────────────────────────────── */}
      {whales && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Activity size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">Whale Activity</h2>
            <span className={`ml-auto badge text-xs ${
              whales.summary.net_flow_signal === 'accumulation' ? 'badge-green' : 'badge-red'
            }`}>
              {whales.summary.net_flow_signal}
            </span>
          </div>

          {/* Exchange flows summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 border-b border-border">
            {whales.exchange_flows.map(ef => (
              <div key={ef.exchange} className="stat-card text-center">
                <p className="text-xs text-text-muted">{ef.exchange}</p>
                <p className={`text-sm font-medium ${ef.btc_net_flow >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {ef.btc_net_flow >= 0 ? '+' : ''}{fmt(ef.btc_net_flow)} BTC
                </p>
                <span className={`badge text-xs mt-1 ${
                  ef.signal === 'accumulation' ? 'badge-green' :
                  ef.signal === 'distribution' ? 'badge-red' : 'badge-blue'
                }`}>{ef.signal}</span>
              </div>
            ))}
          </div>

          {/* Recent transactions */}
          <ul className="divide-y divide-border max-h-80 overflow-y-auto">
            {whales.transactions.slice(0, 10).map(tx => (
              <li key={tx.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className={`badge text-xs ${
                    tx.type === 'exchange_deposit' ? 'badge-red' :
                    tx.type === 'exchange_withdrawal' ? 'badge-green' : 'badge-blue'
                  }`}>
                    {tx.type.replace(/_/g, ' ')}
                  </span>
                  <div>
                    <span className="text-text-primary font-medium">{fmt(tx.amount)} {tx.coin}</span>
                    <span className="text-text-muted text-xs ml-2">({fmtCompact(tx.usd_value)})</span>
                  </div>
                </div>
                <div className="text-right text-xs text-text-muted">
                  <div>{tx.from} &rarr; {tx.to}</div>
                  <div>{tx.blockchain}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      </div>{/* end inner space-y-6 */}
      </DataSection>
    </div>
  )
}
